import type { Tone } from '../../components/StatusBadge';
import type { AuthedRequest } from '../auth/AuthProvider';

/** Mirrors GET/PUT /api/v1/devices/{id}/recording (ADR-012). */
export interface RecordingPolicy {
  enabled: boolean;
  /** '' records the device's main (first) profile. */
  profile_token: string;
  /** Retention in minutes (0.66.0): a day option × 1440, or 5 to 1440 in steps of 5. */
  retention_minutes: number;
  /** Whole days of the retention; 0 when it is in hours. */
  retention_days: number;
  /**
   * device_disabled and tenant_suspended: paused because the equipment (or
   * its recorder) is disabled or the organisation is suspended (0.58);
   * site_suspended and client_suspended: the account or its client is
   * suspended or cancelled (0.59).
   */
  state: RecordingState;
  last_error: RecordingErrorCode | null;
  state_since: string | null;
  /** False when this deployment has no recording storage. */
  available: boolean;
  mode: RecordingMode;
  /** Weekly windows of a scheduled policy; empty when continuous. */
  schedule: RecordingWindow[];
  /** Zone the schedule is read in: the account's, else the organisation's. */
  time_zone: string;
  /** Event mode settings (ADR-040); absent from servers before 0.42. */
  event?: EventSettings;
}

export type RecordingState =
  | 'idle'
  | 'recording'
  | 'error'
  | 'off_schedule'
  | 'device_disabled'
  | 'site_suspended'
  | 'client_suspended'
  | 'tenant_suspended'
  | 'waiting_event';

export type RecordingMode = 'continuous' | 'schedule' | 'event';

/** Which events keep a recording and how much around each (ADR-040). */
export interface EventSettings {
  kinds: string[];
  pre_seconds: number;
  post_seconds: number;
}

export const defaultEvent: EventSettings = { kinds: [], pre_seconds: 10, post_seconds: 60 };

/** Events that can keep a recording, grouped as on the screen. */
export const eventTriggerGroups: { label: string; kinds: string[] }[] = [
  { label: 'Vídeo', kinds: ['video.motion', 'video.tamper', 'video.signal_loss'] },
  {
    label: 'Análise de vídeo',
    kinds: ['video.person', 'video.vehicle', 'video.line_crossing', 'video.intrusion'],
  },
  { label: 'Som e alarme', kinds: ['audio.sound', 'alarm.input'] },
];

export const preSecondsOptions = [0, 5, 10, 15, 30, 60, 120];
/** From 60 s: an event-mode camera always records at least that after each event (0.66.1). */
export const postSecondsOptions = [60, 120, 300, 600];

/** A weekly recording window: days 0 (Sunday) to 6, "HH:MM" local times. */
export interface RecordingWindow {
  days: number[];
  start: string;
  /** At or before start: the window ends the next day. */
  end: string;
}

export type RecordingErrorCode =
  | 'device_disabled'
  | 'no_streams'
  | 'unknown_profile'
  | 'blocked'
  | 'source_unavailable'
  | 'engine_unavailable'
  | 'device_not_found'
  | 'internal';

export interface RecordingPolicyBody {
  enabled: boolean;
  profile_token: string;
  retention_minutes: number;
  mode: RecordingMode;
  schedule: RecordingWindow[];
  event: EventSettings;
}

export const recordingKey = (deviceId: string) => ['recording', deviceId] as const;

export const getRecording = (req: AuthedRequest, deviceId: string) =>
  req<RecordingPolicy>('GET', `/api/v1/devices/${encodeURIComponent(deviceId)}/recording`);

export const putRecording = (req: AuthedRequest, deviceId: string, body: RecordingPolicyBody) =>
  req<RecordingPolicy>('PUT', `/api/v1/devices/${encodeURIComponent(deviceId)}/recording`, body);

export const recordingErrorLabels: Record<RecordingErrorCode, string> = {
  device_disabled: 'O dispositivo está desabilitado.',
  no_streams: 'O dispositivo não tem perfis de vídeo. Execute o teste de conexão.',
  unknown_profile: 'O perfil escolhido não existe mais no dispositivo. Escolha outro.',
  blocked: 'O endereço da câmera foi bloqueado pela política de rede.',
  source_unavailable:
    'A câmera não está enviando vídeo. A gravação retoma sozinha quando ela voltar.',
  engine_unavailable: 'O servidor de mídia não respondeu. A gravação retoma quando ele voltar.',
  device_not_found: 'O dispositivo não foi encontrado.',
  internal: 'Erro interno ao gravar. Consulte os logs do servidor.',
};

/** Why a paused recording is not running, for tooltips. */
export const recordingPausedHints: Partial<Record<RecordingState, string>> = {
  device_disabled:
    'O equipamento está desativado. A gravação retoma sozinha quando ele for reativado.',
  site_suspended:
    'A conta está suspensa ou cancelada. A gravação retoma sozinha quando ela for reativada.',
  client_suspended:
    'O cliente está suspenso ou cancelado. A gravação retoma sozinha quando ele for reativado.',
  tenant_suspended: 'A empresa está suspensa. A gravação retoma sozinha quando ela for reativada.',
};

export function recordingView(p: RecordingPolicy): { tone: Tone; label: string } {
  if (!p.enabled) return { tone: 'neutral', label: 'Desligada' };
  switch (p.state) {
    case 'recording':
      return { tone: 'ok', label: 'Gravando' };
    case 'error':
      return { tone: 'crit', label: 'Com erro' };
    case 'off_schedule':
      return { tone: 'neutral', label: 'Fora do horário' };
    case 'waiting_event':
      return { tone: 'neutral', label: 'Aguardando evento' };
    case 'device_disabled':
      return { tone: 'neutral', label: 'Equipamento desativado' };
    case 'site_suspended':
      return { tone: 'neutral', label: 'Conta suspensa' };
    case 'client_suspended':
      return { tone: 'neutral', label: 'Cliente suspenso' };
    case 'tenant_suspended':
      return { tone: 'neutral', label: 'Empresa suspensa' };
    default:
      return { tone: 'neutral', label: 'Iniciando' };
  }
}

/** Days of the week, Sunday first (the API's numbering). */
export const weekdayLabels = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export const weekdayNames = [
  'domingo',
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
];

/** Most windows a schedule takes (the API's limit). */
export const maxWindows = 14;

/** A new window: weekdays, business hours. */
export const defaultWindow: RecordingWindow = {
  days: [1, 2, 3, 4, 5],
  start: '08:00',
  end: '18:00',
};

/** A window ends the next day when its end is at or before its start. */
export const crossesMidnight = (w: RecordingWindow) => w.end <= w.start;

/** "Seg a Sex, 08:00–18:00": a schedule in words, for lists. */
export function scheduleSummary(schedule: RecordingWindow[]): string {
  return schedule
    .map((w) => {
      const days = [...w.days].sort((a, b) => a - b);
      const run = days.length > 2 && days.every((d, i) => i === 0 || d === (days[i - 1] ?? 0) + 1);
      const label =
        days.length === 7
          ? 'Todos os dias'
          : run
            ? `${weekdayLabels[days[0] ?? 0] ?? ''} a ${weekdayLabels[days.at(-1) ?? 0] ?? ''}`
            : days.map((d) => weekdayLabels[d]).join(', ');
      return `${label}, ${w.start}–${w.end === '00:00' ? '24:00' : w.end}`;
    })
    .join('; ');
}

/** The retentions offered in days (scope review of 08/10/2026). */
export const retentionPresets = [1, 3, 7, 15, 30, 60, 90];

/** Minutes in a day: a retention of whole days shows in days. */
export const minutesPerDay = 1440;

/** Retention in hours (0.66.0): 0 to 24 hours and 00 to 55 minutes, from 5 min to 24 h. */
export const retentionHourOptions = Array.from({ length: 25 }, (_, i) => i);
export const retentionMinuteOptions = Array.from({ length: 12 }, (_, i) => i * 5);

export type RetentionUnit = 'days' | 'hours';

/**
 * Splits a retention for the form: a whole number of days (a preset, or a legacy value outside them) opens in days (24 h comes
 * back as 1 dia), anything else in hours and minutes. The other unit starts
 * at 1 dia or 24 h 00.
 */
export function splitRetention(total: number): {
  unit: RetentionUnit;
  days: number;
  hours: number;
  minutes: number;
} {
  const days = total / minutesPerDay;
  if (Number.isInteger(days) && days > 0) return { unit: 'days', days, hours: 24, minutes: 0 };
  return { unit: 'hours', days: 1, hours: Math.floor(total / 60), minutes: total % 60 };
}

/** A retention in hours is 5 min to 24 h, in steps of 5 min. */
export function validHoursRetention(total: number): boolean {
  return total >= 5 && total <= minutesPerDay && total % 5 === 0;
}

/** "5 min", "1 h", "1 h 30", "16 h", "1 dia", "7 dias". */
export function formatRetention(total: number): string {
  if (total > 0 && total % minutesPerDay === 0) {
    const days = total / minutesPerDay;
    return days === 1 ? '1 dia' : `${String(days)} dias`;
  }
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${String(m)} min`;
  if (m === 0) return `${String(h)} h`;
  return `${String(h)} h ${String(m).padStart(2, '0')}`;
}

/** A device's recording policy for lists (GET /api/v1/recording-policies). */
export interface PolicySummary extends RecordingPolicy {
  device_id: string;
  /** Name of the recorded profile; '' when unknown. */
  profile_name: string;
}

export const policiesKey = (ids: string[]) => ['recording-policies', ...ids] as const;

/** Policies of up to 200 devices; devices without a policy are absent. */
export async function listRecordingPolicies(
  req: AuthedRequest,
  deviceIds: string[],
): Promise<PolicySummary[]> {
  if (deviceIds.length === 0) return [];
  const q = new URLSearchParams();
  for (const id of deviceIds) q.append('device_id', id);
  return (
    await req<{ items: PolicySummary[] }>('GET', `/api/v1/recording-policies?${q.toString()}`)
  ).items;
}
